import { computed, toValue } from 'vue'
import { MIME_TYPES } from '@deepcloud/deep-ui-utils'
import type { MimeTypes } from '@deepcloud/deep-ui-utils'

export const DEFAULT_PREVIEW_WIDTH = 700

function isTouchDevice() {
  return window.matchMedia('(pointer: coarse)').matches
}

export function clamp(value: number, min: number, max: number) {
  if (value > max) return max
  else if (value < min) return min
  else return value
}

const ZOOM_STEPS = isTouchDevice()
  ? [0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3]
  : [0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2]

const FILE_PREVIEW_SUPPORTED_MIME_TYPES = [
  MIME_TYPES.MIME_TYPE_PDF,
  MIME_TYPES.MIME_TYPE_PNG,
  MIME_TYPES.MIME_TYPE_JPEG,
  MIME_TYPES.MIME_TYPE_GIF,
  MIME_TYPES.MIME_TYPE_WEBP,
  MIME_TYPES.MIME_TYPE_SVG,
  MIME_TYPES.MIME_TYPE_DEEPPAY_PAYMENT_ORDER,
  MIME_TYPES.MIME_TYPE_JSON,
  MIME_TYPES.MIME_TYPE_TXT,
  // video
  MIME_TYPES.MIME_TYPE_VIDEO_MP4,
  MIME_TYPES.MIME_TYPE_VIDEO_WEBM,
  // audio
  MIME_TYPES.MIME_TYPE_AUDIO_MPEG,
  MIME_TYPES.MIME_TYPE_AUDIO_WAV,
]

const FILE_EDIT_SUPPORTED_MIME_TYPES = [
  MIME_TYPES.MIME_TYPE_DOCX,
  MIME_TYPES.MIME_TYPE_XLSX,
  MIME_TYPES.MIME_TYPE_PPTX,
  // TXT is not supported because the content is not updated from OO on DeepBox
  // MIME_TYPES.MIME_TYPE_TXT
]

/**
 * MIME types can include parameters (e.g. "text/plain;charset=UTF-8"),
 * so we strip everything after the first ";" to get the base type
 * before checking against the supported list.
 */
export const isMimeTypeSupported = (
  mimeType: MimeTypes,
  mimeTypes: MimeTypes[],
) =>
  mimeTypes.some(
    (supported) =>
      mimeType === supported || mimeType.startsWith(`${supported};`),
  )

export const isMimeTypeViewSupported = (mimeType: MimeTypes) =>
  isMimeTypeSupported(mimeType, FILE_PREVIEW_SUPPORTED_MIME_TYPES)

export const isMimeTypeEditSupported = (mimeType: MimeTypes) =>
  isMimeTypeSupported(mimeType, FILE_EDIT_SUPPORTED_MIME_TYPES)

export function usePreview(
  defaultPreviewWidth: Ref<number> = ref(DEFAULT_PREVIEW_WIDTH),
) {
  const zoom = ref(1) // base zoom factor (1 = 100%)

  const minZoom = computed(() => ZOOM_STEPS[0])
  const maxZoom = computed(() => ZOOM_STEPS[ZOOM_STEPS.length - 1])
  const zoomPercentage = computed(() => Math.round(toValue(zoom) * 100))

  const previewWidth = computed(() => defaultPreviewWidth.value * zoom.value)

  const previewZoomPercentage = computed(
    () => `${(zoom.value * 100).toFixed(2)}%`,
  )

  function previewZoomIn(currentZoom: number) {
    const next = ZOOM_STEPS.find((value) => currentZoom < value)
    return next ?? ZOOM_STEPS[ZOOM_STEPS.length - 1]
  }

  function previewZoomOut(currentZoom: number) {
    const next = ZOOM_STEPS.findLast((value) => currentZoom > value)
    return next ?? ZOOM_STEPS[0]
  }

  function applyZoom(newZoom: number, maxWidth: number) {
    // Clamp based on max available width
    zoom.value = Math.min(newZoom, maxWidth / defaultPreviewWidth.value)
  }

  return {
    zoom,
    minZoom,
    maxZoom,
    zoomPercentage,
    previewWidth,
    previewZoomPercentage,
    previewZoomIn,
    previewZoomOut,
    applyZoom,
  }
}
